/**
 * Application-wide UI preferences that aren't tied to a specific project —
 * persisted so they survive reloads but don't bloat the serialized project
 * file.
 *
 * ## Why everything lives here (#998)
 *
 * Preferences used to be written wherever they happened to be used: 45 raw
 * `localStorage` calls across eight files, each with its own key constant, its
 * own `try/catch`, and its own hand-rolled "validate the string against a
 * union, else fall back" dance. That had three costs — there was no seam at
 * which to change the storage backend (so preferences were welded to one
 * browser on one computer), the disabled-storage discipline was per-call-site
 * and easy to forget, and nothing could answer "what do we persist?" except a
 * grep.
 *
 * So: one storage seam ({@link setPreferenceStore}), one declaration per
 * preference with its key / codec / default together, and one registry
 * ({@link listPreferences}) that IS the inventory. Call sites get a typed
 * getter and setter and never see a storage API. Syncing preferences to the
 * user's account (#999) becomes a matter of swapping the store.
 *
 * ## Reading is lazy, then cached
 *
 * A preference reads through on first `get()` and caches from then on, so
 * bootstrap order can't matter and repeated reads are free. `set()` writes
 * through and notifies subscribers. Every storage touch is wrapped, so a
 * disabled or full store degrades to defaults silently rather than throwing
 * during boot.
 *
 * ## What is deliberately NOT a preference
 *
 * `src/storage/` persists project DOCUMENTS (large, IndexedDB-first) — a
 * different concern that stays put. Another record lives in the same browser
 * store but isn't a user preference and must never be synced to an account:
 * the pending-image-upload receipt. It uses {@link readPreferenceEntry} and
 * friends — the same wrapped storage seam, but outside the typed registry.
 */
import type {
  ConstructionEditAction,
  ConstructionKind,
  ConstructionSubMode,
} from "./tools/ConstructionTool";
import type { ShapeKind } from "./tools/ShapeTool";
import type { CutoutKind } from "./tools/CutoutTool";
import type { StitchKind } from "./tools/StitchTool";
import type { ToolSettings } from "./tools/EditorContext";
import { MAX_FREEHAND_SMOOTHNESS } from "./model/freehandSmoothing";
import { THEME_PROFILE_IDS, type ThemeProfileId } from "./themeProfiles";

// ---------------------------------------------------------------------------
// The storage seam
// ---------------------------------------------------------------------------

/** Where preferences are kept. The one place that knows about a storage API —
 *  swap it to layer account sync (#999) on top, or to isolate a test. */
export interface PreferenceStore {
  read(key: string): string | null;
  write(key: string, value: string): void;
  remove(key: string): void;
}

/** The default store: this browser's `localStorage`. */
const browserStore: PreferenceStore = {
  read: (key) => localStorage.getItem(key),
  write: (key, value) => localStorage.setItem(key, value),
  remove: (key) => localStorage.removeItem(key),
};

let store: PreferenceStore = browserStore;

/**
 * Every store touch goes through these, so a store that throws — storage
 * disabled, a private window at quota, later a network-backed account store
 * (#999) — degrades to defaults instead of throwing during bootstrap. Wrapping
 * HERE rather than inside `browserStore` means the guarantee holds for whatever
 * store is installed, not just the built-in one.
 */
function readEntry(key: string): string | null {
  try {
    return store.read(key);
  } catch {
    return null;
  }
}

function writeEntry(key: string, value: string): void {
  try {
    store.write(key, value);
  } catch {
    /* storage disabled / quota — silent */
  }
}

function removeEntry(key: string): void {
  try {
    store.remove(key);
  } catch {
    /* storage disabled — silent */
  }
}

/** Swap the backing store. Pass null to restore the browser default. Every
 *  cached value is dropped, so the next read comes from the new store. */
export function setPreferenceStore(next: PreferenceStore | null): void {
  store = next ?? browserStore;
  for (const p of registry) p.invalidate();
}

/** Read a raw stored entry through the same wrapped seam, for records that
 *  aren't user preferences (see the module docs) and so stay out of the typed
 *  registry. */
export function readPreferenceEntry(key: string): string | null {
  return readEntry(key);
}

/** Write a raw stored entry. See {@link readPreferenceEntry}. */
export function writePreferenceEntry(key: string, value: string): void {
  writeEntry(key, value);
}

/** Delete a raw stored entry. See {@link readPreferenceEntry}. */
export function removePreferenceEntry(key: string): void {
  removeEntry(key);
}

// ---------------------------------------------------------------------------
// The typed registry
// ---------------------------------------------------------------------------

/** One persisted preference: a typed value with a default, a storage key, and
 *  change notification. */
export interface Preference<T> {
  /** The stored key. Stable forever — changing one loses users' settings. */
  readonly key: string;
  get(): T;
  set(value: T): void;
  /** Drop the stored value and fall back to the default. */
  reset(): void;
  /** Notify on change. Returns an unsubscribe fn. */
  subscribe(fn: (value: T) => void): () => void;
}

interface RegisteredPreference extends Preference<unknown> {
  /** Forget the cached value so the next read hits the store. */
  invalidate(): void;
}

const registry: RegisteredPreference[] = [];

/** Every declared preference, in declaration order — the typed inventory the
 *  ticket asks for. What a future export / import / reset-all covers, and what
 *  account sync (#999) would carry. */
export function listPreferences(): readonly Preference<unknown>[] {
  return registry;
}

/** Clear every preference back to its default. */
export function resetAllPreferences(): void {
  for (const p of registry) p.reset();
}

/**
 * Declare a preference.
 *
 * `decode` returns null when the stored string is unusable — absent, corrupt,
 * out of range, or a value from an older shape of the app — and the default is
 * used instead. `encode` returning null DELETES the entry, which is how a
 * "follow the system" or "no preference" state is stored as absence rather
 * than as a magic string.
 */
function definePreference<T>(opts: {
  key: string;
  fallback: T;
  decode: (raw: string) => T | null;
  encode: (value: T) => string | null;
}): Preference<T> {
  const { key, fallback, decode, encode } = opts;
  // `undefined` means "not read yet" — distinct from a decoded null value.
  let cached: { value: T } | null = null;
  const listeners = new Set<(value: T) => void>();

  const pref: RegisteredPreference = {
    key,
    get(): T {
      if (!cached) {
        const raw = readEntry(key);
        cached = { value: raw === null ? fallback : (decode(raw) ?? fallback) };
      }
      return cached.value;
    },
    set(value: unknown): void {
      const next = value as T;
      // A primitive that already holds this value is a no-op — no write, no
      // listener call. Objects deliberately DON'T dedupe: `toolSettingsPref` is
      // set with the same mutated settings object on every change, so an
      // identity check would silently make every save after the first a no-op.
      if (next === null || typeof next !== "object") {
        // Read through first so the comparison is against the STORED value,
        // not just against whatever a previous `set` happened to cache.
        pref.get();
        if (cached && Object.is(cached.value, next)) return;
      }
      cached = { value: next };
      const encoded = encode(next);
      if (encoded === null) removeEntry(key);
      else writeEntry(key, encoded);
      for (const fn of listeners) fn(next);
    },
    reset(): void {
      removeEntry(key);
      cached = null;
      const value = pref.get() as T;
      for (const fn of listeners) fn(value);
    },
    subscribe(fn: (value: unknown) => void): () => void {
      listeners.add(fn as (value: T) => void);
      return () => {
        listeners.delete(fn as (value: T) => void);
      };
    },
    invalidate(): void {
      cached = null;
    },
  };
  registry.push(pref);
  return pref as Preference<T>;
}

// ---------------------------------------------------------------------------
// Codecs
// ---------------------------------------------------------------------------

/** A boolean stored as "1" / "0". `fallback` is what an absent entry means —
 *  it differs per preference (the cutting mat is on by default, the split
 *  properties panel is off), which is exactly why it's declared next to the
 *  key rather than assumed. */
function boolPreference(key: string, fallback: boolean): Preference<boolean> {
  return definePreference<boolean>({
    key,
    fallback,
    decode: (raw) => (raw === "1" ? true : raw === "0" ? false : null),
    encode: (v) => (v ? "1" : "0"),
  });
}

/**
 * A value from a fixed set of strings, or null when nothing usable is stored.
 *
 * This replaces the "validate the string against a union, else fall back"
 * block that was copy-pasted seven times — and, unlike those copies, the
 * allowed values are checked against the union at COMPILE time by
 * {@link allOf}, so a newly added kind can't silently stop persisting.
 */
function enumPreference<T extends string>(
  key: string,
  values: readonly T[],
): Preference<T | null> {
  return definePreference<T | null>({
    key,
    fallback: null,
    decode: (raw) => (values.includes(raw as T) ? (raw as T) : null),
    encode: (v) => v,
  });
}

/** A free-form string (a tool id, validated by the caller against a registry
 *  this module can't see), or null when nothing is stored. */
function stringPreference(key: string): Preference<string | null> {
  return definePreference<string | null>({
    key,
    fallback: null,
    decode: (raw) => raw,
    encode: (v) => v,
  });
}

/** A number inside `[min, max]`, or null when nothing usable is stored. The
 *  range check is part of the codec so a hand-edited or stale out-of-range
 *  value can never reach a tool. */
function numberPreference(
  key: string,
  range: { min: number; max: number },
): Preference<number | null> {
  return definePreference<number | null>({
    key,
    fallback: null,
    decode: (raw) => {
      const n = parseFloat(raw);
      return Number.isFinite(n) && n >= range.min && n <= range.max ? n : null;
    },
    encode: (v) => (v === null ? null : String(v)),
  });
}

/** A JSON blob, or null when absent / unparseable. The caller owns the shape:
 *  these are records whose migration rules live with the code that consumes
 *  them. */
function jsonPreference<T>(key: string): Preference<T | null> {
  return definePreference<T | null>({
    key,
    fallback: null,
    decode: (raw) => {
      try {
        const parsed: unknown = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? (parsed as T) : null;
      } catch {
        return null;
      }
    },
    encode: (v) => (v === null ? null : JSON.stringify(v)),
  });
}

/**
 * Spell out every member of a string union as a runtime list.
 *
 * The list must be EXHAUSTIVE: the return type resolves to `never` when a
 * union member is missing, so adding a new draw kind and forgetting it here is
 * a compile error rather than a preference that quietly stops round-tripping.
 * Extra values that aren't in the union are rejected the same way.
 */
function allOf<T extends string>() {
  return <L extends readonly T[]>(
    list: [T] extends [L[number]] ? L : never,
  ): L => list;
}

// ---------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------

export type Units = "mm" | "in";

/**
 * Where leatherworkers work in inches (#177).
 *
 * NOT a general imperial/metric map of the world. It is a list of the places
 * where this CRAFT is measured in inches, which is not the same question: the
 * UK and Canada are metric countries whose leather trades still buy 8 oz hide
 * and 1/4" strap, while the handful of officially non-metric countries outside
 * this list have no leatherworking convention pulling either way. So it stays
 * an explicit list rather than something derived from a units-of-measure
 * dataset, and anyone it guesses wrong for changes it in one click.
 */
const INCH_REGIONS = new Set(["US", "CA", "GB", "AU"]);

/** The region subtag of a BCP-47 tag: `en-US` → `US`, `zh-Hant-TW` → `TW`,
 *  `en` → null. */
function localeRegion(tag: string): string | null {
  try {
    const region = new Intl.Locale(tag).region;
    if (region) return region.toUpperCase();
  } catch {
    // A malformed tag from a stored preference or an exotic browser — fall
    // through to the pattern rather than taking the whole module down.
  }
  // Language, an optional script, then the region: two letters or three
  // digits. Underscores as well as hyphens, since that spelling does turn up.
  const m = /^[A-Za-z]{2,3}(?:[-_][A-Za-z]{4})?[-_]([A-Za-z]{2}|\d{3})\b/.exec(
    tag,
  );
  return m ? m[1]!.toUpperCase() : null;
}

/**
 * Which units to start a first-time visitor in, from their browser's language
 * list (#177). The FIRST tag that names a region decides — the list is in the
 * user's own order of preference, so an `en-US` ahead of a `de-DE` means they
 * would rather read US English, region and all.
 *
 * A tag with no region (a bare `en`, `de`) says nothing about where its user
 * is, so it is skipped rather than guessed at. Nothing left to go on means
 * metric, which is what most of the world and all of the file format use.
 */
export function unitsForLocales(locales: readonly string[]): Units {
  for (const tag of locales) {
    const region = localeRegion(tag);
    if (region) return INCH_REGIONS.has(region) ? "in" : "mm";
  }
  return "mm";
}

/** The browser's language list, most-preferred first. */
function navigatorLocales(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  if (navigator.languages?.length) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}

const unitsPref = definePreference<Units>({
  key: "leather-art:units-preference",
  // A DEFAULT, not a setting: it applies only while nothing is stored, so the
  // first toolbar toggle or Settings change ends its involvement for good.
  fallback: unitsForLocales(navigatorLocales()),
  decode: (raw) => (raw === "in" ? "in" : raw === "mm" ? "mm" : null),
  encode: (v) => v,
});

/** What a first-time visitor on this device would get — what the Settings row
 *  reports when nothing has been chosen yet. */
export function localeDefaultUnits(): Units {
  return unitsForLocales(navigatorLocales());
}

export function getUnits(): Units {
  return unitsPref.get();
}

export function setUnits(u: Units) {
  unitsPref.set(u);
}

export function onUnitsChange(fn: (u: Units) => void): () => void {
  return unitsPref.subscribe(fn);
}

/** Whether paired length inputs render both the primary and the secondary
 *  unit (mm + in), or only the primary unit selected via {@link getUnits}.
 *  Defaults to `false` — most users only ever read / type in one system,
 *  and dropping the second field declutters every dimensioned row in the
 *  properties panel. The Settings dialog flips it on for users who want
 *  the live cross-unit readout. */
const showSecondaryUnitsPref = boolPreference(
  "leather-art:show-secondary-units",
  false,
);

export function getShowSecondaryUnits(): boolean {
  return showSecondaryUnitsPref.get();
}

export function setShowSecondaryUnits(v: boolean) {
  showSecondaryUnitsPref.set(v);
}

export function onShowSecondaryUnitsChange(
  fn: (v: boolean) => void,
): () => void {
  return showSecondaryUnitsPref.subscribe(fn);
}

// ---------------------------------------------------------------------------
// Appearance + layout
// ---------------------------------------------------------------------------

/** Explicit color-mode override. Null means "follow prefers-color-scheme",
 *  stored as the ABSENCE of the entry — which is why the toggle's third state
 *  needs no magic string. */
export const colorModePref = enumPreference<"light" | "dark">(
  "leather-art:mode",
  ["light", "dark"],
);

/** Selected theme profile (#190). Null means the plain palette, stored as the
 *  ABSENCE of the entry — the same trick `colorModePref` uses for "system", so
 *  the default look needs no id of its own. Composes with the color mode
 *  rather than replacing it: a profile supplies its own light AND dark chrome.
 *  Values are the ids in `themeProfiles.ts`; an unrecognized one (a profile
 *  since renamed, or a preference written by a newer build) decodes to null
 *  and quietly falls back to plain. */
export const themeProfilePref = enumPreference<ThemeProfileId>(
  "leather-art:theme-profile",
  THEME_PROFILE_IDS,
);

/** Toolbar shows icons + labels (expanded) rather than icons alone. Off by
 *  default so first-time users see the original compact toolbar. */
export const toolbarExpandedPref = boolPreference(
  "leather-art:toolbar-expanded",
  false,
);

/** Properties + stamps split into their own sidebar instead of docking into
 *  the parts list. (Portrait orientation forces the split regardless.) */
export const propertiesDetachedPref = boolPreference(
  "leather-art:properties-detached",
  false,
);

/** The properties panel's TOOL scope lives in a sidebar of its own rather than
 *  stacked above the selected object's properties (#339). Off by default: it
 *  spends another 280px of screen on panels, which only pays for itself on a
 *  wide display. Narrow / portrait layouts force it back off regardless. */
export const toolPropertiesSplitPref = boolPreference(
  "leather-art:tool-properties-split",
  false,
);

/**
 * Mirror the workspace for a left-handed maker (#332): tools down the RIGHT
 * edge, panels down the left, canvas between them as before.
 *
 * The point is the drawing hand, not handedness as an identity — on a tablet,
 * where this app is mainly used, the hand holding the stylus sits on top of
 * whichever edge it reaches across, hiding the tools it is reaching for. A
 * left-hander loses the toolbar; mirrored, they lose the empty margin instead.
 * Off by default because right-handers are the majority, not because the
 * layout is more correct.
 */
export const leftHandedPref = boolPreference(
  "leather-art:left-handed-layout",
  false,
);

/**
 * "Don't show me preview features" (#1204). Preview features are the paid
 * ones, marked with the Preview badge (#1120); during the public preview
 * everyone gets them as early-access features. This is the explicit opt-out:
 * off by default, so preview features show unless the user turns them off.
 * Later, showing them will also depend on a membership the app can check.
 */
export const hidePreviewFeaturesPref = boolPreference(
  "leather-art:hide-preview-features",
  false,
);

/** Part-list rows show their summary chips. On by default. */
export const layerChipsVisiblePref = boolPreference(
  "leather-art:layers-chips-visible",
  true,
);

/** Cutting-mat background. On by default for first-time users. */
export const matVisiblePref = boolPreference("leather-art:mat-visible", true);

/** Assembled vs spread part layout (#652). Null (nothing stored) means the
 *  default, spread — parts laid out for cutting. */
export const partLayoutPref = enumPreference<"spread" | "assembled">(
  "leather-art:part-layout",
  ["spread", "assembled"],
);

// ---------------------------------------------------------------------------
// Tool state
// ---------------------------------------------------------------------------

/** Last-used tool (#806). A free string: the valid set is the tool registry,
 *  which lives in `app.ts`, so the caller validates and picks its own
 *  fallback. */
export const lastToolPref = stringPreference("leather-art:tool");

/** The whole `ToolSettings` blob, persisted on every settings change so tool
 *  parameters survive a reload. Its migrations (the pre-#98 `snapToAxis`
 *  toggle) live with the code that consumes it. */
export const toolSettingsPref = jsonPreference<
  Partial<ToolSettings> & { snapToAxis?: boolean }
>("leather-art:settings");

/** Construction sub-mode + Edit sub-sub-action. Restored even when the active
 *  tool isn't Construction, so switching back lands where the user left it. */
export const constructionSubModePref = enumPreference<ConstructionSubMode>(
  "leather-art:construction-submode",
  allOf<ConstructionSubMode>()([
    "draw",
    "select",
    "edit",
    "delete",
    "offset",
    "simple-offset",
    "mirror",
    "simple-mirror",
    "round",
  ]),
);

/** Note the DELIBERATE omission of "delete": it's a top-level sub-mode now
 *  (#670 follow-up), not an Edit sub-sub-tool, so a pref written by an older
 *  build must fall through rather than restore. That's why this list is
 *  hand-written instead of `allOf`. */
export const constructionEditActionPref =
  enumPreference<ConstructionEditAction>(
    "leather-art:construction-edit-action",
    [
      "drag",
      "smooth-toggle",
      "add-point",
      "slice",
    ] satisfies readonly ConstructionEditAction[],
  );

/** Last picked kind per drawing tool (#972) — construction plus the three
 *  leather modes, so every drawing tool behaves the same across a reload. Each
 *  falls back to that tool's OWN default when absent, which is why an absent
 *  pref leaves Stitch on Offset and the others on Rectangle. */
export const constructionKindPref = enumPreference<ConstructionKind>(
  "leather-art:construction-kind",
  allOf<ConstructionKind>()([
    "polyline",
    "polygon",
    "rect",
    "ellipse",
    "slot",
    "line",
    "freehand",
  ]),
);

export const leatherShapeKindPref = enumPreference<ShapeKind>(
  "leather-art:leather-shape-kind",
  allOf<ShapeKind>()([
    "offset",
    "rect",
    "ellipse",
    "freehand",
    "polygon",
    "slot",
  ]),
);

export const cutoutKindPref = enumPreference<CutoutKind>(
  "leather-art:cutout-kind",
  allOf<CutoutKind>()([
    "offset",
    "rect",
    "ellipse",
    "freehand",
    "polygon",
    "slot",
  ]),
);

export const stitchKindPref = enumPreference<StitchKind>(
  "leather-art:stitch-kind",
  allOf<StitchKind>()([
    "offset",
    "polyline",
    "polygon",
    "line",
    "rect",
    "ellipse",
    "slot",
    "freehand",
  ]),
);

/** Default smoothness for the freehand Draw tool (#416). */
export const constructionFreehandSmoothnessPref = numberPreference(
  "leather-art:construction-freehand-smoothness",
  { min: 0, max: MAX_FREEHAND_SMOOTHNESS },
);

/** Leather's freehand smoothness default (#1016). Shares construction's RANGE
 *  (one meaning for one gesture, per the #1016 follow-up) but keeps its OWN
 *  key — they're separate presets, so moving one must not move the other. */
export const leatherFreehandSmoothnessPref = numberPreference(
  "leather-art:leather-freehand-smoothness",
  { min: 0, max: MAX_FREEHAND_SMOOTHNESS },
);

/** Draw a faint dot on every point the cursor could snap to (#367).
 *
 *  Off by default — the dots are an aid for learning where the snap targets
 *  are, and a permanently speckled canvas would be noise for someone who
 *  already knows. Only shown while point snapping is actually armed, so the
 *  dots always mean "this would snap right now" rather than "this might". */
export const showSnapCandidatesPref = boolPreference(
  "leather-art:show-snap-candidates",
  false,
);

// ---------------------------------------------------------------------------
// Calibration
// ---------------------------------------------------------------------------

/** Sanity bounds on the calibration multiplier: a typo like "10 mm" for a
 *  100 mm line would push it to 10×, "1000 mm" to 0.1×. Clamped inside ±3× of
 *  the CSS-spec baseline, which still covers every realistic display. Declared
 *  here because the codec enforces them on read; `calibration.ts` imports them
 *  for its clamp-on-write, so there is one range rather than two. */
export const CALIBRATION_MIN = 0.3;
export const CALIBRATION_MAX = 3.0;

/** Screen-size calibration multiplier. Out-of-range or corrupt values decode
 *  to null, which the calibration module reads as 1× (uncalibrated). */
export const calibrationPref = numberPreference(
  "leather-art:calibration-mult",
  {
    min: CALIBRATION_MIN,
    max: CALIBRATION_MAX,
  },
);
